import { QuestionViewFrame } from '../../questions/types'
import DevLog from '../devlog/Module'
import { ModuleID } from '../types'
import { ITimeline, ITimelineDurations, ITimelineEvent, ITimelineModule, TimeSeconds, TimeStamp } from './types'

export const TIMELINE_OPTIONAL_EVENT_TYPE_PREFIX = '@'

const DEBUG_TIMELINE = 'development' === process.env.NODE_ENV

let loaded = false
let msToSec: (ms: TimeStamp) => TimeSeconds = ms => Math.round(ms / 10) / 100

const TimelineModule: ITimelineModule = {
  get id(): ModuleID {
    return ModuleID.TIMELINE
  },
  config({ msToSec: overrideMsToSec } = {}) {
    if (overrideMsToSec) {
      msToSec = overrideMsToSec
    }
    return TimelineModule
  },
  // eslint-disable-next-line @typescript-eslint/require-await
  async load() {
    loaded = true
    return TimelineModule
  },
  get loaded() {
    return loaded
  },
  createTimeline(id = `timeline-${Date.now()}`) {
    let startTime: TimeStamp = null
    const frameTimes: Map<
      QuestionViewFrame,
      {
        started: boolean
        start: TimeStamp
        interactionStart: TimeStamp
        time: TimeSeconds
        full_time: TimeSeconds
      }
    > = new Map()
    const frameTimesOrder: QuestionViewFrame[] = []
    const log: ITimelineEvent[] = []

    const timeline: ITimeline = {
      get id() {
        return id
      },
      start(time = Date.now()) {
        if (!timeline.started) {
          startTime = time
          if (DEBUG_TIMELINE) {
            DevLog._moduleLog(TimelineModule, id, 'start', new Date(startTime).toLocaleString())
          }
        }
        return timeline
      },
      restart(time = Date.now()) {
        log.splice(0, log.length)
        startTime = time
        frameTimes.clear()
        frameTimesOrder.splice(0, frameTimesOrder.length)
        return timeline
      },
      get started() {
        return Boolean(startTime)
      },
      reset() {
        startTime = null
        log.splice(0, log.length)
        frameTimes.clear()
        if (DEBUG_TIMELINE) {
          DevLog._moduleLog(TimelineModule, id, 'reset', Date.now().toLocaleString())
        }
        return timeline
      },
      log(event, time = Date.now()) {
        if (!timeline.started) {
          timeline.start(time)
        }
        const logEvent = Object.freeze({ ...event, time: msToSec(time - startTime) })
        log.push(logEvent)
        if (DEBUG_TIMELINE) {
          DevLog._moduleLog(TimelineModule, id, 'log', logEvent)
        }
        return timeline
      },
      _log(event, time = Date.now()) {
        return timeline.log({ ...event, type: `${TIMELINE_OPTIONAL_EVENT_TYPE_PREFIX}${event.type}` }, time)
      },
      checkpoint(event, time = Date.now()) {
        startTime = time
        const logEvent = Object.freeze({ ...event, time: msToSec(0) })
        log.push(logEvent)
        if (DEBUG_TIMELINE) {
          DevLog._moduleLog(TimelineModule, id, 'checkpoint', logEvent)
        }
        return timeline
      },
      _checkpoint(event, time = Date.now()) {
        return timeline.checkpoint({ ...event, type: `${TIMELINE_OPTIONAL_EVENT_TYPE_PREFIX}${event.type}` }, time)
      },
      frameStart(frame, time = Date.now()) {
        if (timeline.started) {
          const frameTime = frameTimes.get(frame)
          if (frameTime) {
            if (!frameTime.started) {
              frameTime.start = time
              frameTime.started = true
              frameTime.interactionStart = time
              if (DEBUG_TIMELINE) {
                DevLog._moduleLog(TimelineModule, id, 'frameStart', frame, msToSec(time - startTime))
              }
            }
          } else {
            frameTimes.set(frame, {
              started: true,
              start: time,
              interactionStart: time,
              time: 0,
              full_time: 0
            })
            frameTimesOrder.push(frame)
            if (DEBUG_TIMELINE) {
              DevLog._moduleLog(TimelineModule, id, 'frameStart', frame, msToSec(time - startTime))
            }
          }
        }
        return timeline
      },
      frameStarted(frame) {
        return Boolean(frameTimes.get(frame)?.started)
      },
      frameInteractionStart(frame, time = Date.now()) {
        if (timeline.started) {
          const frameTime = frameTimes.get(frame)
          if (frameTime && frameTime.started) {
            if (DEBUG_TIMELINE) {
              DevLog._moduleLog(TimelineModule, id, 'frameInteractionStart', frame, msToSec(time - startTime))
            }
            frameTime.interactionStart = time
          } else {
            return timeline.frameStart(frame, time)
          }
        }
        return timeline
      },
      frameCommit(frame, time = Date.now()) {
        if (timeline.started) {
          const frameTime = frameTimes.get(frame)
          if (frameTime && frameTime.started) {
            if (DEBUG_TIMELINE) {
              DevLog._moduleLog(TimelineModule, id, 'frameCommit', frame, msToSec(time - startTime))
            }
            frameTime.time = frameTime.time + msToSec(time - frameTime.interactionStart)
            frameTime.full_time = frameTime.full_time + msToSec(time - frameTime.start)
            frameTime.start = time
            frameTime.started = false
            frameTime.interactionStart = time
          }
        }
        return timeline
      },
      frameDiscard(frame) {
        frameTimes.delete(frame)
        const frameTimesOrderIndex = frameTimesOrder.indexOf(frame)
        if (0 <= frameTimesOrderIndex) {
          frameTimesOrder.splice(frameTimesOrderIndex, 1)
        }
        return timeline
      },
      getLog(filter = 'exclude-optional') {
        return 'exclude-optional' === filter
          ? log.filter(filterTimelineOptionalEvents)
          : 'all' === filter
          ? log.slice()
          : 'function' === typeof filter
          ? log.filter(filter)
          : log.slice()
      },
      getDurations() {
        const durations: ITimelineDurations = {
          time: [],
          full_time: []
        }
        for (const frame of frameTimesOrder) {
          const frameTime = frameTimes.get(frame)
          if (frameTime) {
            if (frameTime.started) {
              // if frame was not committed - calculate frame time to current time
              const now = Date.now()
              durations.time.push(frameTime.time + msToSec(now - frameTime.interactionStart))
              durations.full_time.push(frameTime.full_time + msToSec(now - frameTime.start))
            } else {
              durations.time.push(frameTime.time)
              durations.full_time.push(frameTime.full_time)
            }
          }
        }
        return durations
      }
    }
    return timeline
  }
}

export default TimelineModule

export function filterTimelineOptionalEvents(event: ITimelineEvent) {
  return event.type.startsWith
    ? !event.type.startsWith(TIMELINE_OPTIONAL_EVENT_TYPE_PREFIX)
    : 0 !== event.type.indexOf(TIMELINE_OPTIONAL_EVENT_TYPE_PREFIX)
}
